import * as reduxApi from 'src/utils/redux-api-v2';
import type {Dispatch, ThunkAction, GetState} from 'src/reducers';
import {
  ANALYTICS_API_V3,
  ANALYTICS_API_V4,
} from 'src/utils/analytics-api-migration';
import type {ApiOptions, ApiPostOptions} from 'src/utils/redux-api';
import type {
  SelectedEntity,
  VoiceFilters,
  SavedDashboardList,
  FilterByID,
  SelectedVoiceCallFilterColumnsData,
  SelectedVoiceFilters,
  VoiceCallActiveTabType,
} from 'src/types/analytics-genesis';
import type {DateRangeWithTimezone} from '@spaced-out/ui-design-system/lib/types';
import {generateUUID} from 'src/utils/page-site';

import {thunkify as flow} from 'src/utils/thunks';
import {isEqual, result} from 'lodash';
import {fetching, key} from 'src/utils/redux';
import {
  allCallRecordsTableColumnsQuery,
  allVoiceflowsTableColumnsQuery,
  allWorkflowsTableColumnsQuery,
} from 'src/utils/analytics-genesis';


const analyticsOptions: ApiOptions = {apiPath: ANALYTICS_API_V4};
const analyticsApiOptions: ApiPostOptions = {apiPath: ANALYTICS_API_V4};

export const SET_CURRENT_DASHBOARD_ID =
  'analytics-voice-call/set-current-dashboard';
export const SET_SELECTED_ENTITIES_FILTER =
  'analytics-voice-call/set-selected-entities-filter';
export const SET_SELECTED_VOICE_FILTERS =
  'analytics-voice-call/set-selected-voice-filters';
export const UPDATE_SELECTED_VOICE_FILTERS =
  'analytics-voice-call/update-selected-voice-filters';
export const SET_SELECTED_DATE_RANGE =
  'analytics-voice-call/set-selected-date-range';
export const CLEAR_SELECTED_FILTERS =
  'analytics-voice-call/clear-selected-filters';
export const RECEIVE_SAVED_DASHBOARDS =
  'analytics-voice-call/receive-saved-dashboards';
export const SET_NAVIGATION_DOWN = 'analytics-voice-call/set-navigation-down';
export const SET_NAVIGATION_UP = 'analytics-voice-call/set-navigation-up';
export const GET_ALL_VOICEFLOWS_TABLE_FILTER_COLUMN =
  'analytics-voice-call/get-all-voiceflows-table-filter-column';
export const SAVE_ALL_VOICEFLOWS_FILTER_COLUMNS =
  'analytics-voice-call/save-all-voiceflows-filter-columns';

type SetCurrentDashboardIdAction = {
  type: typeof SET_CURRENT_DASHBOARD_ID;
  payload: string | null | undefined;
};

type SetSelectedEntitiesFiltersAction = {
  type: typeof SET_SELECTED_ENTITIES_FILTER;
  payload: Array<SelectedEntity>;
};

type SetSelectedVoiceFiltersAction = {
  type: typeof SET_SELECTED_VOICE_FILTERS;
  payload: VoiceFilters;
};

type UpdateSelectedVoiceFiltersAction = {
  type: typeof UPDATE_SELECTED_VOICE_FILTERS;
  payload: VoiceFilters;
};

type SetSelectedDateRangeAction = {
  type: typeof SET_SELECTED_DATE_RANGE;
  payload: DateRangeWithTimezone;
};

type ClearSelectedFiltersAction = {
  type: typeof CLEAR_SELECTED_FILTERS;
};

type ReceiveSavedDashboardsAction = {
  type: typeof RECEIVE_SAVED_DASHBOARDS;
  payload: SavedDashboardList;
};

type SetNavigationDownAction = {
  type: typeof SET_NAVIGATION_DOWN;
  payload: boolean;
};

type SetNavigationUpAction = {
  type: typeof SET_NAVIGATION_UP;
  payload: boolean;
};

export const SET_SELECTED_VOICE_TAB =
  'analytics-voice-call/set-selected-voice-tab';

type SetSelectedVoiceTabAction = {
  type: typeof SET_SELECTED_VOICE_TAB;
  payload: VoiceCallActiveTabType;
};

export const RECEIVE_ALL_VOICEFLOWS_TABLE_COLUMN_SELECTION =
  'analytics-voice-call/receive-all-voiceflows-table-column-selection';

type ReceiveAllVoiceflowsTableColumnSelectionAction = {
  type: typeof RECEIVE_ALL_VOICEFLOWS_TABLE_COLUMN_SELECTION;
  payload: SelectedVoiceCallFilterColumnsData;
};

export const RECEIVE_ALL_WORKFLOWS_TABLE_COLUMN_SELECTION =
  'analytics-voice-call/receive-all-workflows-table-column-selection';

type ReceiveAllWorkflowsTableColumnSelectionAction = {
  type: typeof RECEIVE_ALL_WORKFLOWS_TABLE_COLUMN_SELECTION;
  payload: SelectedVoiceCallFilterColumnsData;
};

export const RECEIVE_ALL_CALL_RECORDS_TABLE_COLUMN_SELECTION =
  'analytics-voice-call/receive-all-call-records-table-column-selection';

type ReceiveAllCallRecordsTableColumnSelectionAction = {
  type: typeof RECEIVE_ALL_CALL_RECORDS_TABLE_COLUMN_SELECTION;
  payload: SelectedVoiceCallFilterColumnsData;
};

export const setSelectedVoiceTab = (
  tab: VoiceCallActiveTabType,
): SetSelectedVoiceTabAction => ({
  type: SET_SELECTED_VOICE_TAB,
  payload: tab,
});
export type AnalyticsVoiceCallAction =
  | SetCurrentDashboardIdAction
  | SetSelectedEntitiesFiltersAction
  | SetSelectedVoiceFiltersAction
  | UpdateSelectedVoiceFiltersAction
  | SetSelectedDateRangeAction
  | ClearSelectedFiltersAction
  | ReceiveSavedDashboardsAction
  | SetNavigationDownAction
  | SetNavigationUpAction
  | ReceiveAllVoiceflowsTableColumnSelectionAction
  | ReceiveAllWorkflowsTableColumnSelectionAction
  | ReceiveAllCallRecordsTableColumnSelectionAction
  | SetSelectedVoiceTabAction;

export const setCurrentDashboardId = (
  dashboardId: string | null | undefined,
): SetCurrentDashboardIdAction => ({
  type: SET_CURRENT_DASHBOARD_ID,
  payload: dashboardId,
});

export const setSelectedEntitiesFilters = (
  entities: Array<SelectedEntity>,
): SetSelectedEntitiesFiltersAction => ({
  type: SET_SELECTED_ENTITIES_FILTER,
  payload: entities,
});

export const setSelectedVoiceFilters = (
  filters: VoiceFilters,
): SetSelectedVoiceFiltersAction => ({
  type: SET_SELECTED_VOICE_FILTERS,
  payload: filters,
});

export const updateSelectedVoiceFilters = (
  filters: VoiceFilters,
): UpdateSelectedVoiceFiltersAction => ({
  type: UPDATE_SELECTED_VOICE_FILTERS,
  payload: filters,
});

export const setSelectedDateRange = (
  dateRange: DateRangeWithTimezone,
): SetSelectedDateRangeAction => ({
  type: SET_SELECTED_DATE_RANGE,
  payload: dateRange,
});

export const clearSelectedFilters = (): ClearSelectedFiltersAction => ({
  type: CLEAR_SELECTED_FILTERS,
});

export const receiveSavedDashboards = (
  dashboards: SavedDashboardList,
): ReceiveSavedDashboardsAction => ({
  type: RECEIVE_SAVED_DASHBOARDS,
  payload: dashboards,
});

export const receiveAllVoiceflowsTableColumnSelection = (
  payload: SelectedVoiceCallFilterColumnsData,
): ReceiveAllVoiceflowsTableColumnSelectionAction => ({
  type: RECEIVE_ALL_VOICEFLOWS_TABLE_COLUMN_SELECTION,
  payload,
});

export const receiveAllWorkflowsTableColumnSelection = (
  payload: SelectedVoiceCallFilterColumnsData,
): ReceiveAllWorkflowsTableColumnSelectionAction => ({
  type: RECEIVE_ALL_WORKFLOWS_TABLE_COLUMN_SELECTION,
  payload,
});

export const receiveAllCallRecordsTableColumnSelection = (
  payload: SelectedVoiceCallFilterColumnsData,
): ReceiveAllCallRecordsTableColumnSelectionAction => ({
  type: RECEIVE_ALL_CALL_RECORDS_TABLE_COLUMN_SELECTION,
  payload,
});

export const setNavigationDown = (
  isDown: boolean,
): SetNavigationDownAction => ({
  type: SET_NAVIGATION_DOWN,
  payload: isDown,
});

export const setNavigationUp = (isUp: boolean): SetNavigationUpAction => ({
  type: SET_NAVIGATION_UP,
  payload: isUp,
});

export const getSavedVoiceCallDashboards: () => ThunkAction<unknown> =
  () => async (dispatch: Dispatch) => {
    try {
      const response = await dispatch(
        reduxApi.get(
          '/voice-workflows/save-filter',
          {
            product: 'voice_call',
            dashboard: 'voice_call',
          },
          {apiPath: ANALYTICS_API_V3},
        ),
      );
      const {
        filters,
      }: {
        filters: SavedDashboardList;
      } = response;
      await dispatch(receiveSavedDashboards(filters));
      const defaultDashboard = filters.find(({is_default}) => is_default);
      if (defaultDashboard) {
        const {id = '', filters = {}} = defaultDashboard;
        const {
          workflow_nodes = [{id: generateUUID()}],
          recipient_type = [],
          workflow_status = [],
          workflow_category = [],
          voice_flow = [],
          voice_call_status = [],
          entity_data_filter = [{id: generateUUID()}],
          selected_date_range = {
            startDateUTC: null,
            startDate: null,
            endDateUTC: null,
            endDate: null,
          },
        } = filters as SelectedVoiceFilters;
        await dispatch(setCurrentDashboardId(id));
        await dispatch(
          setSelectedEntitiesFilters(
            entity_data_filter as Array<SelectedEntity>,
          ),
        );
        await dispatch(
          setSelectedVoiceFilters({
            workflow_nodes,
            workflow_status,
            workflow_category,
            voice_flow,
            recipient_type,
            voice_call_status,
          }),
        );
        await dispatch(setSelectedDateRange(selected_date_range));
      }
    } catch (error: any) {
      console.error('Error fetching saved dashboards:', error);
    }
  };

export const saveVoiceCallDashboard: (
  name: string,
  filters: VoiceFilters,
) => ThunkAction<unknown> = flow()(
  (name, filters) => async (dispatch: Dispatch) => {
    try {
      const dashboardData = {
        id: generateUUID(),
        name,
        filters,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      await dispatch(
        reduxApi.post(
          'voice-call-analytics/save-dashboard',
          dashboardData,
          {},
          analyticsApiOptions,
        ),
      );

      // Refresh the saved dashboards list
      dispatch(getSavedVoiceCallDashboards());
    } catch (error: any) {
      console.error('Error saving dashboard:', error);
    }
  },
);

export const getAllVoiceflowsTableFilterColumn: () => ThunkAction<unknown> =
  flow(
    key(() => 'getAllVoiceflowsTableFilterColumn'),
    fetching(),
  )(() => async (dispatch: Dispatch) => {
    try {
      const payload: SelectedVoiceCallFilterColumnsData = await dispatch(
        reduxApi.get(
          '/voice-workflows/column-selection',
          allVoiceflowsTableColumnsQuery,
          {apiPath: ANALYTICS_API_V3},
        ),
      );
      dispatch(receiveAllVoiceflowsTableColumnSelection(payload));
    } catch (error: any) {
      console.error('Error fetching voiceflows table columns:', error);
    }
  });

export const saveAllVoiceflowsFilterColumns: (
  columnData: SelectedVoiceCallFilterColumnsData,
) => ThunkAction<unknown> = flow()(
  (columnData) => async (dispatch: Dispatch) => {
    try {
      await dispatch(
        reduxApi.post(
          '/voice-workflows/column-selection',
          columnData,
          {},
          {apiPath: ANALYTICS_API_V3},
        ),
      );
    } catch (error: any) {
      console.error('Error saving voiceflows table columns:', error);
    }
  },
);

export const getAllWorkflowsTableFilterColumn: () => ThunkAction<unknown> =
  flow(
    key(() => 'getAllWorkflowsTableFilterColumn'),
    fetching(),
  )(() => async (dispatch: Dispatch) => {
    try {
      const payload: SelectedVoiceCallFilterColumnsData = await dispatch(
        reduxApi.get(
          '/voice-workflows/column-selection',
          allWorkflowsTableColumnsQuery,
          {apiPath: ANALYTICS_API_V3},
        ),
      );
      dispatch(receiveAllWorkflowsTableColumnSelection(payload));
    } catch (error: any) {
      console.error('Error fetching workflows table columns:', error);
    }
  });

export const saveAllWorkflowsFilterColumns: (
  columnData: SelectedVoiceCallFilterColumnsData,
) => ThunkAction<unknown> = flow()(
  (columnData) => async (dispatch: Dispatch) => {
    try {
      await dispatch(
        reduxApi.post(
          '/voice-workflows/column-selection',
          columnData,
          {},
          {apiPath: ANALYTICS_API_V3},
        ),
      );
    } catch (error: any) {
      console.error('Error saving workflows table columns:', error);
    }
  },
);

export const getAllCallRecordsTableFilterColumn: () => ThunkAction<unknown> =
  flow(
    key(() => 'getAllCallRecordsTableFilterColumn'),
    fetching(),
  )(() => async (dispatch: Dispatch) => {
    try {
      const payload: SelectedVoiceCallFilterColumnsData = await dispatch(
        reduxApi.get(
          '/voice-workflows/column-selection',
          allCallRecordsTableColumnsQuery,
          {apiPath: ANALYTICS_API_V3},
        ),
      );
      dispatch(receiveAllCallRecordsTableColumnSelection(payload));
    } catch (error: any) {
      console.error('Error fetching call records table columns:', error);
    }
  });

export const saveAllCallRecordsFilterColumns: (
  columnData: SelectedVoiceCallFilterColumnsData,
) => ThunkAction<unknown> = flow()(
  (columnData) => async (dispatch: Dispatch) => {
    try {
      await dispatch(
        reduxApi.post(
          '/voice-workflows/column-selection',
          columnData,
          {},
          {apiPath: ANALYTICS_API_V3},
        ),
      );
    } catch (error: any) {
      console.error('Error saving call records table columns:', error);
    }
  },
);

export const getNodesFromWorkflow: (
  workflowId: string,
) => ThunkAction<unknown> = flow(
  key((workflowId: string) => `getNodesFromWorkflow-${workflowId}`),
  fetching(),
)((workflowId: string) => async (dispatch: Dispatch) => {
  try {
    const {result: nodes}: {result: Array<FilterByID>} = await dispatch(
      reduxApi.post(
        '/all-nodes-for-workflow',
        {workflow_id: workflowId},
        {},
        {apiPath: ANALYTICS_API_V3},
      ),
    );
    return nodes;
  } catch (error) {
    console.error('Error fetching nodes for workflow:', error);
    return [];
  }
});
